/**
 * RetakeModal Component
 *
 * Modal dialog for course retake confirmation.
 * Supports both full course reset and partial (chapter-level) reset.
 *
 * Features:
 * - Full reset: Resets entire course progress
 * - Partial reset: Uses ChapterSelector for chapter selection
 * - Multi-language support (Hebrew, Arabic, Russian, English)
 * - Loading and error states
 * - Accessible design using Paragon components
 *
 * @version 2.0.0
 */

import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from '@edx/frontend-platform/i18n';
import {
  Button,
  ModalDialog,
  ActionRow,
  Alert,
  Form,
  Spinner,
} from '@edx/paragon';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamationTriangle,
  faLayerGroup,
} from '@fortawesome/free-solid-svg-icons';

import ChapterSelector from './ChapterSelector';
import messages from '../messages';
import './RetakeModal.css';

// ===== Constants =====

const RESET_TYPES = {
  FULL: 'full',
  PARTIAL: 'partial',
};

// ===== Sub-components =====

/**
 * Warning alert displayed at top of modal
 */
const WarningAlert = () => {
  const intl = useIntl();
  return (
    <Alert variant="warning" className="retake-modal__warning">
      <FontAwesomeIcon
        icon={faExclamationTriangle}
        className="retake-modal__warning-icon"
      />
      <span>
        <strong>{intl.formatMessage(messages.retakeModalWarningLabel)}</strong>
        {intl.formatMessage(messages.retakeModalWarningText)}
      </span>
    </Alert>
  );
};

/**
 * Info box showing what will be reset
 */
const ResetInfoBox = ({ resetType, selectedCount }) => {
  const intl = useIntl();
  return (
    <Alert variant="info" className="retake-modal__info-box">
      <h5 className="retake-modal__info-title">{intl.formatMessage(messages.retakeModalInfoTitle)}</h5>
      <ul className="retake-modal__info-list">
        {resetType === RESET_TYPES.PARTIAL ? (
          <>
            <li>{intl.formatMessage(messages.retakeModalInfoProgressPartial, { count: selectedCount })}</li>
            <li>{intl.formatMessage(messages.retakeModalInfoGradesPartial)}</li>
            <li>{intl.formatMessage(messages.retakeModalInfoAttemptsPartial)}</li>
          </>
        ) : (
          <>
            <li>{intl.formatMessage(messages.retakeModalInfoProgress)}</li>
            <li>{intl.formatMessage(messages.retakeModalInfoGrades)}</li>
            <li>{intl.formatMessage(messages.retakeModalInfoAttempts)}</li>
          </>
        )}
        <li className="retake-modal__info-warning">{intl.formatMessage(messages.retakeModalInfoIrreversible)}</li>
      </ul>
    </Alert>
  );
};

ResetInfoBox.propTypes = {
  resetType: PropTypes.oneOf([RESET_TYPES.FULL, RESET_TYPES.PARTIAL]).isRequired,
  selectedCount: PropTypes.number,
};

ResetInfoBox.defaultProps = {
  selectedCount: 0,
};

/**
 * Reset type selector (radio buttons)
 */
const ResetTypeSelector = ({
  value, onChange, disabled,
}) => {
  const intl = useIntl();
  return (
    <div className="retake-modal__type-section">
      <h5 className="retake-modal__section-title">
        <FontAwesomeIcon icon={faLayerGroup} className="retake-modal__section-icon" />
        {intl.formatMessage(messages.retakeModalTypeSectionTitle)}
      </h5>

      <Form.RadioSet
        name="resetType"
        value={value}
        onChange={onChange}
        isInline={false}
        className="retake-modal__type-options"
      >
        <Form.Radio
          value={RESET_TYPES.FULL}
          disabled={disabled}
          className="retake-modal__type-option"
        >
          <div className="retake-modal__option-content">
            <strong className="retake-modal__option-title">{intl.formatMessage(messages.retakeModalTypeFullTitle)}</strong>
            <small className="retake-modal__option-desc">
              {intl.formatMessage(messages.retakeModalTypeFullDesc)}
            </small>
          </div>
        </Form.Radio>

        <Form.Radio
          value={RESET_TYPES.PARTIAL}
          disabled={disabled}
          className="retake-modal__type-option"
        >
          <div className="retake-modal__option-content">
            <strong className="retake-modal__option-title">{intl.formatMessage(messages.retakeModalTypePartialTitle)}</strong>
            <small className="retake-modal__option-desc">
              {intl.formatMessage(messages.retakeModalTypePartialDesc)}
            </small>
          </div>
        </Form.Radio>
      </Form.RadioSet>
    </div>
  );
};

ResetTypeSelector.propTypes = {
  value: PropTypes.oneOf([RESET_TYPES.FULL, RESET_TYPES.PARTIAL]).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

ResetTypeSelector.defaultProps = {
  disabled: false,
};

// ===== Main Component =====

/**
 * RetakeModal - Main modal component for course retake
 */
const RetakeModal = ({
  isOpen,
  onClose,
  onConfirm,
  isProcessing,
  error,
  success,
  allowPartialReset,
  chapters,
  isLoadingChapters,
  chaptersError,
}) => {
  // ===== Hooks =====
  const intl = useIntl();

  // ===== State =====
  const [resetType, setResetType] = useState(RESET_TYPES.FULL);
  const [selectedChapterIds, setSelectedChapterIds] = useState([]);
  const [validationError, setValidationError] = useState(null);

  // ===== Effects =====

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setResetType(RESET_TYPES.FULL);
      setSelectedChapterIds([]);
      setValidationError(null);
    }
  }, [isOpen]);

  // Clear validation error when chapters are selected
  useEffect(() => {
    if (selectedChapterIds.length > 0) {
      setValidationError(null);
    }
  }, [selectedChapterIds]);

  // ===== Handlers =====

  /**
   * Handle reset type change
   */
  const handleResetTypeChange = useCallback((e) => {
    const newType = e.target.value;
    setResetType(newType);
    setValidationError(null);

    // Clear chapter selection when switching to full reset
    if (newType === RESET_TYPES.FULL) {
      setSelectedChapterIds([]);
    }
  }, []);

  /**
   * Handle chapter selection change from ChapterSelector
   */
  const handleChapterSelectionChange = useCallback((newSelection) => {
    setSelectedChapterIds(newSelection);
  }, []);

  /**
   * Handle modal close
   */
  const handleClose = useCallback(() => {
    if (!isProcessing) {
      onClose();
    }
  }, [isProcessing, onClose]);

  /**
   * Handle confirm button click with validation
   */
  const handleConfirmClick = useCallback(() => {
    // Validate partial reset has chapters selected
    if (resetType === RESET_TYPES.PARTIAL && selectedChapterIds.length === 0) {
      setValidationError(intl.formatMessage(messages.retakeModalValidationError));
      return;
    }

    // Call parent confirm handler
    onConfirm({
      resetType,
      chapterIds: resetType === RESET_TYPES.PARTIAL ? selectedChapterIds : [],
    });
  }, [resetType, selectedChapterIds, onConfirm, intl]);

  // ===== Computed Values =====

  const isConfirmDisabled = isProcessing
    || success
    || (resetType === RESET_TYPES.PARTIAL && selectedChapterIds.length === 0);

  const showChapterSelector = resetType === RESET_TYPES.PARTIAL && allowPartialReset;

  const confirmButtonText = (() => {
    if (isProcessing) {
      return intl.formatMessage(messages.retakeModalProcessing);
    }
    if (resetType === RESET_TYPES.PARTIAL && selectedChapterIds.length > 0) {
      return intl.formatMessage(messages.retakeModalConfirmPartial, { count: selectedChapterIds.length });
    }
    return intl.formatMessage(messages.retakeModalConfirmFull);
  })();

  // ===== Render =====

  return (
    <ModalDialog
      title={intl.formatMessage(messages.retakeModalTitle)}
      isOpen={isOpen}
      onClose={handleClose}
      hasCloseButton={!isProcessing}
      size="lg"
      className="retake-modal"
    >
      <ModalDialog.Header>
        <ModalDialog.Title className="retake-modal__title">
          {intl.formatMessage(messages.retakeModalTitle)}
        </ModalDialog.Title>
      </ModalDialog.Header>

      <ModalDialog.Body className="retake-modal__body">
        {/* Warning */}
        <WarningAlert />

        {/* Info Box */}
        <ResetInfoBox
          resetType={resetType}
          selectedCount={selectedChapterIds.length}
        />

        {/* Reset Type Selection - Only if partial reset is allowed */}
        {allowPartialReset && chapters && chapters.length > 0 && (
          <ResetTypeSelector
            value={resetType}
            onChange={handleResetTypeChange}
            disabled={isProcessing}
          />
        )}

        {/* Chapter Selection - Only for partial reset */}
        {showChapterSelector && (
          <ChapterSelector
            chapters={chapters}
            selectedChapterIds={selectedChapterIds}
            onSelectionChange={handleChapterSelectionChange}
            isLoading={isLoadingChapters}
            error={chaptersError}
            disabled={isProcessing}
            title={intl.formatMessage(messages.retakeModalChapterSelectorTitle)}
            showSelectAll
          />
        )}

        {/* Validation Error */}
        {validationError && (
          <Alert variant="danger" className="retake-modal__alert">
            {validationError}
          </Alert>
        )}

        {/* API Error */}
        {error && (
          <Alert variant="danger" className="retake-modal__alert">
            {error}
          </Alert>
        )}

        {/* Success Message */}
        {success && (
          <Alert variant="success" className="retake-modal__alert">
            {intl.formatMessage(messages.retakeModalSuccessMessage)}
          </Alert>
        )}
      </ModalDialog.Body>

      <ModalDialog.Footer className="retake-modal__footer">
        <ActionRow className="retake-modal__actions">
          <Button
            variant="tertiary"
            onClick={handleClose}
            disabled={isProcessing}
          >
            {intl.formatMessage(messages.retakeModalCancel)}
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmClick}
            disabled={isConfirmDisabled}
          >
            {isProcessing && (
              <Spinner
                animation="border"
                size="sm"
                className="retake-modal__spinner"
              />
            )}
            {confirmButtonText}
          </Button>
        </ActionRow>
      </ModalDialog.Footer>
    </ModalDialog>
  );
};

RetakeModal.propTypes = {
  /** Whether the modal is open */
  isOpen: PropTypes.bool.isRequired,
  /** Callback when modal is closed */
  onClose: PropTypes.func.isRequired,
  /** Callback when reset is confirmed. Receives { resetType, chapterIds } */
  onConfirm: PropTypes.func.isRequired,
  /** Whether reset is currently processing */
  isProcessing: PropTypes.bool.isRequired,
  /** Error message to display */
  error: PropTypes.string,
  /** Whether reset was successful */
  success: PropTypes.bool.isRequired,
  /** Whether partial reset is allowed for this course */
  allowPartialReset: PropTypes.bool,
  /** List of chapters for partial reset selection */
  chapters: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    display_name: PropTypes.string,
    displayName: PropTypes.string,
    position: PropTypes.number,
    subsections_count: PropTypes.number,
    subsectionsCount: PropTypes.number,
    units_count: PropTypes.number,
    unitsCount: PropTypes.number,
    has_graded_content: PropTypes.bool,
    hasGradedContent: PropTypes.bool,
  })),
  /** Whether chapters are currently loading */
  isLoadingChapters: PropTypes.bool,
  /** Error message for chapter loading failure */
  chaptersError: PropTypes.string,
};

RetakeModal.defaultProps = {
  error: null,
  allowPartialReset: false,
  chapters: [],
  isLoadingChapters: false,
  chaptersError: null,
};

export default RetakeModal;
